import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { modalLeagueName } from "./model";
import { FAVICON_SRC, IconCheck, IconClear, IconClose, IconSearch } from "./icons";

function CheckBox({ checked, onChange }) {
  return (
    <span className="relative inline-flex shrink-0">
      <input type="checkbox" className="peer sr-only" checked={checked} onChange={onChange} />
      <span className="w-4 h-4 border border-[var(--color-gray-light)] rounded bg-white peer-checked:bg-[var(--color-primary)] peer-checked:border-[var(--color-primary)] flex items-center justify-center">
        {checked ? <IconCheck /> : null}
      </span>
    </span>
  );
}

function initialDraft(leagues, selectedIds) {
  if (selectedIds == null) return new Set(leagues.map((league) => String(league.id)));
  return new Set([...selectedIds].map(String));
}

export default function LeagueFilterModal({ leagues, selectedIds, onClose, onApply }) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [draft, setDraft] = useState(() => initialDraft(leagues, selectedIds));
  const [overflow, setOverflow] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return leagues;
    return leagues.filter((league) => modalLeagueName(league).toLowerCase().includes(q));
  }, [leagues, q]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    const check = () => setOverflow(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [filtered, searching]);

  const allIds = leagues.map((league) => String(league.id));
  const allChecked = allIds.length > 0 && allIds.every((id) => draft.has(id));

  const toggleId = (id) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirm = () => {
    const every = allIds.every((id) => draft.has(id));
    onApply(every ? null : new Set(draft));
    onClose();
  };

  return createPortal(
    <div
      className="kbv-kq-v32 fixed inset-0 z-100 bg-black/50 backdrop-blur-[1px] flex items-center justify-center px-2 max-sm:py-[10%]"
      onClick={onClose}
    >
      <div
        className="bg-white flex kbv-col w-full md:w-auto max-w-full md:max-w-[90vw] h-[100%] max-h-[95vh] rounded shadow-lg md:shadow-2xl px-2 relative overflow-hidden"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex kbv-col h-full w-full md:w-[520px] lg:w-[640px] max-w-full">
          <div className="flex items-center justify-between sticky top-0 bg-white py-[15.5px] px-1 md:px-6 z-10 md:border-b md:border-gray-100">
            <div className="font-bold leading-[1.2] text-sm md:text-base text-stroke-dark">Lọc giải đấu</div>
            <button type="button" className="hover:opacity-70 transition-opacity p-1 -mr-1" onClick={onClose}>
              <IconClose />
            </button>
          </div>

          <div className="flex-1 flex kbv-col px-1 md:px-6 min-h-0 md:bg-gray-50/10">
            <div className="flex kbv-col justify-center py-3 px-1">
              {searching ? (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 flex items-center bg-gray-100 rounded px-2 py-1">
                    <IconSearch className="mr-2 opacity-50" size={18} />
                    <input
                      className="flex-1 bg-transparent text-sm outline-none text-stroke-dark"
                      placeholder="Tìm kiếm giải đấu..."
                      autoFocus
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                    />
                    {query ? (
                      <button
                        type="button"
                        className="ml-1 p-0.5 rounded-full bg-gray-400 text-white hover:bg-gray-500"
                        onClick={() => setQuery("")}
                      >
                        <IconClear className="invert" />
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="text-sm font-medium text-stroke-dark px-1"
                    onClick={() => {
                      setSearching(false);
                      setQuery("");
                    }}
                  >
                    Hủy
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="font-bold leading-[1.2] text-sm text-surface-900">
                    Lọc theo giải đấu ({draft.size}/{leagues.length}):
                  </div>
                  <button type="button" className="active:scale-95" onClick={() => setSearching(true)}>
                    <IconSearch />
                  </button>
                </div>
              )}
            </div>

            <div
              ref={listRef}
              className={clsx(
                "flex-1 min-h-0 overflow-y-auto league-filter-scrollbar",
                overflow && "sm:-mr-[9px]"
              )}
            >
              {!q && leagues.length > 0 ? (
                <label className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-100 rounded transition-colors">
                  <span className="text-sm font-medium text-stroke-dark leading-[1.2]">Tất cả giải đấu</span>
                  <CheckBox
                    checked={allChecked}
                    onChange={() => setDraft(allChecked ? new Set() : new Set(allIds))}
                  />
                </label>
              ) : null}

              {filtered.length === 0 ? (
                <div className="py-8 h-[80%] flex kbv-col items-center">
                  <img src={FAVICON_SRC} alt="KeoBongVip logo" className="w-[9rem] h-[7.375rem] my-4" />
                  <p className="text-muted text-[0.75rem] text-center p-4">Không tìm thấy giải đấu nào</p>
                </div>
              ) : (
                filtered.map((league) => {
                  const id = String(league.id);
                  const checked = draft.has(id);
                  return (
                    <label
                      key={id}
                      className="flex items-center justify-between px-2 py-[8.5px] cursor-pointer hover:bg-gray-100 rounded transition-colors"
                    >
                      <span className="flex items-center gap-1 min-w-0">
                        {league.logo ? (
                          <span className="w-6 h-6 rounded-full overflow-hidden shrink-0">
                            <img src={league.logo} alt="" className="w-6 h-6 object-contain" width={24} height={24} />
                          </span>
                        ) : (
                          <span className="w-5 h-5 bg-gray-300 rounded-full shrink-0" />
                        )}
                        <span className="text-sm font-medium text-stroke-dark leading-[1.2] truncate">
                          {modalLeagueName(league)}
                        </span>
                      </span>
                      <CheckBox checked={checked} onChange={() => toggleId(id)} />
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 py-[18px] md:px-6 md:border-t md:border-gray-100">
            <button
              type="button"
              className="w-full py-[11px] px-3 active:scale-95 text-xs md:text-sm font-medium leading-[1.2] rounded text-stroke-dark bg-foreground md:bg-gray-100 md:hover:bg-gray-200"
              onClick={() => setDraft(new Set(allIds))}
            >
              Đặt lại
            </button>
            <button
              type="button"
              className="w-full py-[11px] px-3 active:scale-95 text-xs md:text-sm font-medium leading-[1.2] rounded text-stroke-dark bg-primary md:hover:brightness-[1.02] md:active:brightness-95 md:shadow-sm"
              onClick={confirm}
            >
              Xác nhận
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
